/**
 * WebsiteSuggestion.tsx
 * Public website suggestion page: validates input, checks URL uniqueness, then submits a suggestion via the public API.
 */

import {
  useState,
  useEffect,
  useCallback,
  type FormEvent,
  type CSSProperties,
} from "react";
import { SearchFocusTarget, User } from "@/App";
import { useApp } from "@/contexts/AppContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import {
  AlertCircle,
  Lightbulb,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import { WebsiteFormFields } from "@/components/website-form/WebsiteFormFields";
import { WebsiteFormCard } from "@/components/website-form/WebsiteFormCard";
import { WebsiteFormConfirmationDialog } from "@/components/website-form/WebsiteFormConfirmationDialog";
import type { WebsiteSuggestion } from "./website-suggest/types";

import { createWebsiteSuggestion } from "@/services/websiteSuggestion.service";
import { getAiCategorySuggestionEnabled } from "@/services/publicSystemSettings.service";
import {
  type WebsiteFormData,
  type WebsiteFormValidationOptions,
} from "@/utils/websiteFormUtils";
import { useWebsiteFormSubmit } from "@/components/website-form/useWebsiteFormSubmit";
import {
  WEBSITE_FORM_LIMITS,
  buildWebsiteSuggestFormConfig,
  createEmptyWebsiteFormData,
} from "@/components/website-form/WebsiteFormConfig";
import { buildSuggestionPayload } from "@/components/website-form/WebsiteFormPayload";

interface WebsiteSuggestProps {
  navigate: (page: string) => void;
  currentUser: User | null;
  onLogout: () => void;
  onSuggest: (suggestion: WebsiteSuggestion) => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
}

type UrlErrorKey = string;

const formatSummaryValue = (value?: string | null) => {
  if (value === null || value === undefined) return "—";
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : "—";
};

interface SummaryEntryProps {
  label: string;
  value: string;
  valueStyle?: CSSProperties;
}

function SummaryEntry({ label, value, valueStyle }: SummaryEntryProps) {
  return (
    <div>
      <p className="text-xs text-secondary">{label}</p>
      <p className="text-sm break-words" style={valueStyle}>
        {value}
      </p>
    </div>
  );
}

export function WebsiteSuggest({
  navigate,
  currentUser,
  onLogout,
  onSuggest,
  onNavigateToSearchPage,
}: WebsiteSuggestProps) {
  const { t, language } = useApp();

  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const infoIconStyle = {
    backgroundColor: "color-mix(in srgb, var(--accent) 18%, var(--background))",
    border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--background))",
  } as const;
  const errorTextStyle = {
    color: "color-mix(in srgb, var(--accent) 82%, var(--foreground))",
  } as const;

  const [formData, setFormData] = useState<WebsiteFormData>(
    createEmptyWebsiteFormData()
  );

  const [categorizationResetKey, setCategorizationResetKey] = useState(0);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedData, setSubmittedData] =
    useState<WebsiteSuggestion | null>(null);
  const [showDesktopSuggestInfo, setShowDesktopSuggestInfo] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1280px)").matches
      : false
  );

  const [aiFeatureEnabled, setAiFeatureEnabled] = useState(true);

  const [fieldErrors, setFieldErrors] = useState({
    title: false,
    url: false,
    description: false,
  });
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [urlErrorKey, setUrlErrorKey] = useState<UrlErrorKey | null>(null);

  const submitWithValidation = useWebsiteFormSubmit({
    t,
    isSubmitting,
    setIsSubmitting,
    setShowValidation,
    setFieldErrors,
    setUrlErrorKey,
  });
  const formConfig = buildWebsiteSuggestFormConfig(
    t,
    <CheckCircle className="w-4 h-4" />
  );

  const scrollToTop = useCallback(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(min-width: 1280px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setShowDesktopSuggestInfo(event.matches);
    };

    setShowDesktopSuggestInfo(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  /**
   * Checks setting if ai suggestions are enabled
   */
  useEffect(() => {
    let isMounted = true;
    const loadAiSetting = async () => {
      const enabled = await getAiCategorySuggestionEnabled();
      if (isMounted) setAiFeatureEnabled(enabled);
    };
    loadAiSetting();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    try {
      const validationOptions: WebsiteFormValidationOptions = {
        formData,
        ids: formConfig.validation.ids,
        max: WEBSITE_FORM_LIMITS,
        required: formConfig.validation.required,
        errorKeys: formConfig.validation.errorKeys,
      };

      await submitWithValidation({
        formData,
        validationOptions,
        onValidSubmit: async (normalizedUrl) => {
          let backendWebsite: any;

          try {
            const payload = buildSuggestionPayload(formData, normalizedUrl);
            backendWebsite = await createWebsiteSuggestion(payload);
          } catch (error: any) {
            if (error?.response?.status === 409) {
              setFieldErrors((prev) => ({ ...prev, url: true }));
              setUrlErrorKey("websiteSuggest.errorUrlExists");
              toast.error(t("websiteSuggest.errorUrlExists"));
              return;
            }

            const errorText =
              error?.response?.data ??
              error?.message ??
              "Unknown error (axios request failed)";
            console.error("Suggestion submit failed:", errorText);
            toast.error(t("websiteSuggest.submitError"));
            return;
          }

          type BackendWebsiteStatus =
            | "SUGGESTED"
            | "ACTIVE"
            | "INACTIVE"
            | "REJECTED"
            | "ARCHIVED"
            | "DELETED";

          const mapBackendStatusToSuggestionStatus = (
            backendStatus?: BackendWebsiteStatus
          ): WebsiteSuggestion["status"] => {
            switch (backendStatus) {
              case "SUGGESTED":
                return "pending";
              case "ACTIVE":
              case "INACTIVE":
              case "ARCHIVED":
                return "approved";
              case "REJECTED":
              case "DELETED":
                return "rejected";
              default:
                return "pending";
            }
          };

          const rawStatus =
            backendWebsite.status as BackendWebsiteStatus | undefined;

          const safeCategoryPathNames =
            formData.categoryPathNames.length > 0
              ? formData.categoryPathNames
              : formData.category1Name
                ? [formData.category1Name]
                : [];

          const safeCategoryPathIds =
            formData.categoryPathIds.length > 0
              ? formData.categoryPathIds
              : formData.category1Id
                ? [formData.category1Id]
                : [];

          const categoryDisplay =
            safeCategoryPathNames.length > 0
              ? safeCategoryPathNames.join(" > ")
              : "";

          const suggestion: WebsiteSuggestion = {
            id: backendWebsite.id ?? Date.now().toString(),
            title: backendWebsite.title ?? formData.title,
            url: backendWebsite.urlLandingPage ?? normalizedUrl,
            description: backendWebsite.description ?? formData.description,
            address: backendWebsite.address ?? formData.address,

            category1: categoryDisplay,

            categoryPathIds: safeCategoryPathIds,
            categoryPathNames: safeCategoryPathNames,

            region: formData.regionName || "",
            regionPathIds: formData.regionPathIds,
            regionPathNames: formData.regionPathNames,

            category1Id: formData.category1Id,
            regionId: formData.regionId,

            zipCode: formData.zipCode || undefined,
            suggestedBy: currentUser?.email || "anonymous",
            suggestedAt: backendWebsite.addedAt ?? new Date().toISOString(),
            status: mapBackendStatusToSuggestionStatus(rawStatus),
          };

          onSuggest(suggestion);
          setSubmittedData(suggestion);
          setShowConfirmation(true);
          setFormData(createEmptyWebsiteFormData());
          setCategorizationResetKey((prev) => prev + 1);

          setFieldErrors({ title: false, url: false, description: false });
          setShowValidation(false);

          toast.success(
            t("websiteSuggest.submitSuccess") ||
              "Suggestion submitted successfully."
          );
        },
      });
    } catch (error) {
      console.error("Failed to submit suggestion:", error);
      toast.error(t("websiteSuggest.submitError"));
    }
  };

  const handleGoToManagement = () => {
    setShowConfirmation(false);
    navigate("manage-websites");
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", "suggestions");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  };

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: "var(--background)" }}
    >
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        currentPage="suggest-website"
        samePageHandlers={{ "suggest-website": scrollToTop }}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-3 rounded-lg" style={infoIconStyle}>
                <Lightbulb className="w-6 h-6" style={iconStyle} />
                </div>
                <div className="min-w-0">
                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-primary-heading">
                      {t("websiteSuggest.title")}
                    </h2>
                    {!showDesktopSuggestInfo && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label={t("websiteSuggest.infoRegisteredCanAdd")}
                            className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600 dark:focus-visible:ring-offset-slate-900"
                            style={iconStyle}
                          >
                            ?
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          align="center"
                          sideOffset={6}
                          className="w-[min(92vw,22rem)] whitespace-normal text-left leading-relaxed break-words [overflow-wrap:anywhere]"
                        >
                          <p className="whitespace-normal break-words [overflow-wrap:anywhere]">
                            {t("websiteSuggest.infoRegisteredCanAdd")}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <p className="text-sm text-secondary">
                    {t("websiteSuggest.subtitle")}
                  </p>
                </div>
              </div>

              {showDesktopSuggestInfo && (
                <div
                  className="flex max-w-2xl items-start gap-2 rounded-lg border px-3 py-2 text-xs"
                  style={{
                    backgroundColor:
                      "color-mix(in srgb, var(--accent) 8%, var(--background))",
                    borderColor:
                      "color-mix(in srgb, var(--accent) 18%, var(--background))",
                    color:
                      "color-mix(in srgb, var(--foreground) 68%, var(--surface))",
                  }}
                >
                  <AlertCircle
                    className="mt-0.5 h-4 w-4 shrink-0"
                    style={iconStyle}
                  />
                  <p className="leading-relaxed">
                    {t("websiteSuggest.infoRegisteredCanAdd")}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-6">
            <WebsiteFormCard
              title={formConfig.card.title}
              description={formConfig.card.description}
              onSubmit={handleSubmit}
              formFields={
                <WebsiteFormFields
                  formData={formData}
                  setFormData={setFormData}
                  t={t}
                  language={language}
                  isSubmitting={isSubmitting}
                  showValidation={showValidation}
                  fieldErrors={fieldErrors}
                  setFieldErrors={setFieldErrors}
                  urlErrorKey={urlErrorKey}
                  onUrlChange={() => setUrlErrorKey(null)}
                  maxTitleLen={WEBSITE_FORM_LIMITS.title}
                  maxUrlLen={WEBSITE_FORM_LIMITS.url}
                  maxDescriptionLen={WEBSITE_FORM_LIMITS.description}
                  maxAddressLen={WEBSITE_FORM_LIMITS.address}
                  maxZipLen={WEBSITE_FORM_LIMITS.zip}
                  primaryTextStyle={primaryTextStyle}
                  secondaryTextStyle={secondaryTextStyle}
                  errorTextStyle={errorTextStyle}
                  iconStyle={iconStyle}
                  aiFeatureEnabled={aiFeatureEnabled}
                  resetKey={categorizationResetKey}
                  {...formConfig.fields}
                />
              }
              submitLabel={formConfig.card.submitLabel}
              submitIcon={formConfig.card.submitIcon}
              cancelLabel={formConfig.card.cancelLabel}
              onCancel={() => navigate("home")}
              isSubmitting={isSubmitting}
              primaryTextStyle={primaryTextStyle}
              secondaryTextStyle={secondaryTextStyle}
            />
          </div>
        </div>
      </main>

      {submittedData && (
        <WebsiteFormConfirmationDialog
          open={showConfirmation}
          onOpenChange={setShowConfirmation}
          title={t("websiteSuggest.successTitle")}
          icon={<CheckCircle className="w-6 h-6" style={iconStyle} />}
          iconContainerStyle={infoIconStyle}
          description={
            <div className="space-y-4 pt-4">
              <p>{t("websiteSuggest.successMessage")}</p>
              <div className="border rounded-lg p-3 space-y-2">
                <SummaryEntry
                  label={t("websiteSuggest.website")}
                  value={formatSummaryValue(submittedData.title)}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteSuggest.url")}
                  value={formatSummaryValue(submittedData.url)}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteSuggest.descriptionLabel")}
                  value={formatSummaryValue(submittedData.description)}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteSuggest.addressLabel")}
                  value={formatSummaryValue(submittedData.address)}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteSuggest.zipCodeLabel")}
                  value={formatSummaryValue(submittedData.zipCode)}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteSuggest.regionLabel")}
                  value={formatSummaryValue(
                    submittedData.regionPathNames &&
                      submittedData.regionPathNames.length > 0
                      ? submittedData.regionPathNames.join(" > ")
                      : submittedData.region
                  )}
                  valueStyle={primaryTextStyle}
                />
                <SummaryEntry
                  label={t("websiteSuggest.category")}
                  value={formatSummaryValue(
                    submittedData.categoryPathNames &&
                      submittedData.categoryPathNames.length > 0
                      ? submittedData.categoryPathNames.join(" > ")
                      : submittedData.category1
                  )}
                  valueStyle={primaryTextStyle}
                />
              </div>
              <p className="text-sm text-secondary">
                {currentUser
                  ? t("websiteSuggest.successRegisteredInfo")
                  : t("websiteSuggest.successAnonymousInfo")}
              </p>
            </div>
          }
          primaryAction={{
            label: t("websiteSuggest.understood"),
            onClick: () => setShowConfirmation(false),
          }}
          secondaryAction={
            currentUser
              ? {
                  label: t("websiteSuggest.goToManagement"),
                  onClick: handleGoToManagement,
                  variant: "outline",
                }
              : undefined
          }
        />
      )}

      <Footer navigate={navigate} />
    </div>
  );
}

export default WebsiteSuggest;
